import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Description,
  Delete,
  AddShoppingCart,
  Sync,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore
} from '@mui/icons-material';
import {
  formatDecimalToYearsMonths,
  formatTsrCalendarDueRangeDisplay,
  getCalendarDueRangeMuiColor
} from '../components/componentFormUtils';
import { getLiveRawForTsr, liveTsrContextFromRecord } from '../../utils/liveTlbUsageMapping';
import {
  computeAssemblyComparison,
  computeComponentSelfComparison,
  findMatchingAssemblyTSR,
  findAirframeAssembly,
  parseValueByUnit,
  formatValueByUnit
} from '../components/tsrComparisonUtils';
import {
  computeComponentRemainingByUnit,
  formatRemainingUnitCell,
  COMPONENT_UNIT_COLUMN_DEFS
} from './componentRemainingByUnit';

function getRemainingUnitChipColor(unitKey, value) {
  if (value == null) return 'default';
  if (value < 0) return 'error';
  if (unitKey === 'days' && value <= 7) return 'warning';
  if (unitKey === 'hours' && value <= 25) return 'warning';
  if (unitKey === 'cycles' && value <= 50) return 'warning';
  if (unitKey === 'landings' && value <= 25) return 'warning';
  return 'default';
}

/**
 * Get "Remaining" display from component's timeSinceRecords.
 * - No records or none with remaining → 'NIL'
 * - Each record with remainingTime: "name: value unit" (unit optional)
 * - Multiple entries shown with one record per line.
 * Handles: missing/empty timeSinceRecords, optional remainingTime, missing label/type, empty value.
 */
function getRemainingDisplay(component) {
  if (!component) return 'NIL';
  const records = component.timeSinceRecords;
  if (!records || !Array.isArray(records) || records.length === 0) return 'NIL';

  const parts = records
    .map((r, index) => {
      const remaining = r?.remainingTime;
      if (!remaining || remaining.value == null) return null;
      const valueStr = String(remaining.value).trim();
      if (valueStr === '') return null;
      const name = (r.label && String(r.label).trim()) || r.type || `Record ${index + 1}`;
      const unit = remaining.unit != null ? String(remaining.unit).trim() : '';
      return unit ? `${name}: ${valueStr} ${unit}` : `${name}: ${valueStr}`;
    })
    .filter(Boolean);

  if (parts.length === 0) return 'NIL';
  return parts.join('\n');
}

/**
 * Calendar due range (start/as-on → projected due) per TSR when unit is Years or Calendar Days.
 */
function renderCalendarDueDisplay(component) {
  if (!component) {
    return (
      <Typography component="span" variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        NIL
      </Typography>
    );
  }
  const records = component.timeSinceRecords;
  if (!records || !Array.isArray(records) || records.length === 0) {
    return (
      <Typography component="span" variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        NIL
      </Typography>
    );
  }

  const lines = records
    .map((r, index) => {
      const line = formatTsrCalendarDueRangeDisplay(r);
      if (!line) return null;
      const name = (r.label && String(r.label).trim()) || r.type || `Record ${index + 1}`;
      const color = getCalendarDueRangeMuiColor(r);
      return (
        <Box key={`${name}-${index}`} sx={{ color, fontSize: '0.75rem', lineHeight: 1.35 }}>
          {`${name}: ${line}`}
        </Box>
      );
    })
    .filter(Boolean);

  if (lines.length === 0) {
    return (
      <Typography component="span" variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        NIL
      </Typography>
    );
  }
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>{lines}</Box>;
}

function formatSignedValueByUnit(numericValue, unit) {
  if (numericValue == null || Number.isNaN(numericValue)) return '';
  const trimmedUnit = String(unit || '').trim();
  const isNegative = Number(numericValue) < 0;
  const signPrefix = isNegative ? '-' : '';

  if (trimmedUnit === 'Hours') {
    const absMinutes = Math.abs(Math.floor(numericValue));
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return `${signPrefix}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  if (trimmedUnit === 'Landings') {
    return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
  }

  if (trimmedUnit === 'Years') {
    return `${signPrefix}${formatDecimalToYearsMonths(Math.abs(numericValue))}`;
  }

  if (trimmedUnit === 'Calendar Days') {
    return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
  }

  if (trimmedUnit === 'Cycles') {
    const v = Math.round(Math.abs(numericValue) * 100) / 100;
    return `${signPrefix}${v}`;
  }

  return `${numericValue}`;
}

function getEarliestDueSummaries(component, assemblies, currentUsageTotals, effectiveTlbUsageMapping = null) {
  if (!component || !Array.isArray(component.timeSinceRecords) || component.timeSinceRecords.length === 0) {
    return { assemblyDue: 'N/A', componentDue: 'N/A', airframeDue: 'N/A', airframeRemainingDue: 'N/A' };
  }

  const currentValues = currentUsageTotals?.values || null;
  const hasNonEmpty = (v) => v != null && String(v).trim() !== '';

  const assemblyLines = [];
  const componentLines = [];
  const airframeLines = [];
  const airframeRemainingLines = [];

  component.timeSinceRecords.forEach((record, index) => {
    const unit =
      record.elapsedTime?.unit ||
      record.limit?.unit ||
      record.remainingTime?.unit ||
      record.installedValue?.unit ||
      '';
    const dueComponentUnit = record.limit?.unit || unit;
    const parentSnapshotStr = record.parentAssemblyValueAtInstallation?.value;
    const remainingStr = record.remainingTime?.value;
    const componentComparison = computeComponentSelfComparison(record);
    const label =
      (record.label && String(record.label).trim()) ||
      record.type ||
      `Record ${index + 1}`;

    // Snapshot-based Due At Parent Assembly
    if (
      parentSnapshotStr != null &&
      String(parentSnapshotStr).trim() !== '' &&
      remainingStr != null &&
      String(remainingStr).trim() !== '' &&
      unit
    ) {
      try {
        const installedNumeric = parseValueByUnit(parentSnapshotStr, unit);
        const remainingNumeric = parseValueByUnit(remainingStr, unit);
        const dueNumeric = installedNumeric + remainingNumeric;
        const parentDueStr = formatValueByUnit(dueNumeric, unit);
        assemblyLines.push(
          unit ? `${label}: ${parentDueStr} ${unit}` : `${label}: ${parentDueStr}`
        );
      } catch (e) {
        // ignore this record for parent assembly summary
      }
    }

    // Live-based Due + Remaining Until Due (mapped TLB field per Client Settings)
    const { raw: liveRaw } = getLiveRawForTsr(
      currentValues,
      effectiveTlbUsageMapping,
      liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
    );
    const limitRaw = record?.limit?.value;
    const airframeAtInstallRaw = record?.airframeValueAtInstallation?.value;
    const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;

    if (
      liveRaw != null &&
      hasNonEmpty(limitRaw) &&
      hasNonEmpty(airframeAtInstallRaw) &&
      hasNonEmpty(componentElapsedAtInstallRaw)
    ) {
      try {
        const airframeLiveNumeric = parseValueByUnit(liveRaw, unit);
        const airframeAtInstallNumeric = parseValueByUnit(airframeAtInstallRaw, unit);
        const componentElapsedAtInstallNumeric = parseValueByUnit(componentElapsedAtInstallRaw, unit);
        const limitNumeric = parseValueByUnit(limitRaw, unit);

        const dueNumeric = airframeAtInstallNumeric + (limitNumeric - componentElapsedAtInstallNumeric);
        const remainingUntilDueNumeric = dueNumeric - airframeLiveNumeric;

        const airframeDueStr = formatValueByUnit(dueNumeric, unit);
        const airframeRemainingStr = formatSignedValueByUnit(remainingUntilDueNumeric, unit);

        airframeLines.push(`${label}: ${airframeDueStr} ${unit}`);
        airframeRemainingLines.push(`${label}: ${airframeRemainingStr} ${unit}`);
      } catch (e) {
        // ignore this record for airframe summary
      }
    }

    // Snapshot-based Due At Component Value
    if (componentComparison?.dueAtComponent) {
      const componentDueStr = componentComparison.dueAtComponent;
      componentLines.push(
        dueComponentUnit ? `${label}: ${componentDueStr} ${dueComponentUnit}` : `${label}: ${componentDueStr}`
      );
    }
  });

  return {
    assemblyDue: assemblyLines.join('\n') || 'N/A',
    componentDue: componentLines.join('\n') || 'N/A',
    airframeDue: airframeLines.join('\n') || 'N/A',
    airframeRemainingDue: airframeRemainingLines.join('\n') || 'N/A'
  };
}

const ComponentTable = ({
  selectedAircraft,
  components,
  assemblies = [],
  canEdit,
  getStatusColor,
  onOpenDocumentDialog,
  onAddComponent,
  onEditComponent,
  onDeleteComponent,
  onAddToInventory,
  onBackfillInventory,
  onRowClick,
  currentUsageTotals = null,
  effectiveTlbUsageMapping = null,
  initialSearchTerm = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [sortBy, setSortBy] = useState('code');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assemblyFilter, setAssemblyFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [timeSinceTypeFilter, setTimeSinceTypeFilter] = useState('ALL');
  const [timeSinceLabelFilter, setTimeSinceLabelFilter] = useState('ALL');
  const [expandedComponentId, setExpandedComponentId] = useState(null);

  const tableColSpan = 16 + COMPONENT_UNIT_COLUMN_DEFS.length;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  useEffect(() => {
    if (initialSearchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);

  const assemblyOptions = useMemo(() => {
    const set = new Set();
    components.forEach((component) => {
      const name = component.assemblyId?.typeName;
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [components]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    components.forEach((component) => {
      if (component.status) set.add(component.status);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [components]);

  const timeSinceTypeOptions = useMemo(() => {
    const set = new Set();
    components.forEach((component) => {
      (component.timeSinceRecords || []).forEach((record) => {
        if (record.type) {
          set.add(record.type);
        }
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [components]);

  const timeSinceLabelOptions = useMemo(() => {
    const set = new Set();
    components.forEach((component) => {
      (component.timeSinceRecords || []).forEach((record) => {
        const label = (record.label || '').trim();
        if (label) {
          set.add(label);
        }
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [components]);

  const handleAssemblyFilterChange = (event) => {
    setAssemblyFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleActiveFilterChange = (event) => {
    setActiveFilter(event.target.value);
    setPage(0);
  };

  const handleTimeSinceTypeFilterChange = (event) => {
    setTimeSinceTypeFilter(event.target.value);
    setPage(0);
  };

  const handleTimeSinceLabelFilterChange = (event) => {
    setTimeSinceLabelFilter(event.target.value);
    setPage(0);
  };

  const handleToggleExpand = (componentId) => {
    setExpandedComponentId((prev) => (prev === componentId ? null : componentId));
  };

  const filteredComponents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return components.filter((component) => {
      if (assemblyFilter !== 'ALL') {
        const assemblyName = component.assemblyId?.typeName || '';
        if (assemblyName !== assemblyFilter) return false;
      }
      if (statusFilter !== 'ALL') {
        if ((component.status || '') !== statusFilter) return false;
      }
      if (activeFilter !== 'ALL') {
        const isActive = component.isActive !== false;
        if (activeFilter === 'ACTIVE' && !isActive) return false;
        if (activeFilter === 'INACTIVE' && isActive) return false;
      }

      const records = component.timeSinceRecords || [];

      if (timeSinceTypeFilter !== 'ALL') {
        const hasType = records.some((r) => r.type === timeSinceTypeFilter);
        if (!hasType) return false;
      }

      if (timeSinceLabelFilter !== 'ALL') {
        const hasLabel = records.some(
          (r) => (r.label || '').trim() === timeSinceLabelFilter
        );
        if (!hasLabel) return false;
      }

      if (!term) return true;

      const values = [
        component.code || '',
        component.partNumber || '',
        component.serialNumber || '',
        component.description || '',
        component.assemblyId?.typeName || '',
        component.position || '',
        component.status || '',
        component.isActive !== false ? 'active' : 'inactive',
        getRemainingDisplay(component)
      ];
      return values.some((v) => v.toLowerCase().includes(term));
    });
  }, [components, searchTerm, assemblyFilter, statusFilter, activeFilter, timeSinceTypeFilter, timeSinceLabelFilter]);

  const remainingByUnitById = useMemo(() => {
    const map = new Map();
    filteredComponents.forEach((component) => {
      const id = String(component._id || component.code || '');
      map.set(
        id,
        computeComponentRemainingByUnit(component, currentUsageTotals, effectiveTlbUsageMapping)
      );
    });
    return map;
  }, [filteredComponents, currentUsageTotals, effectiveTlbUsageMapping]);

  const handleSortRequest = useCallback((columnId) => {
    setPage(0);
    setSortBy((prev) => {
      if (prev === columnId) {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return columnId;
    });
  }, []);

  const sortedComponents = useMemo(() => {
    const list = [...filteredComponents];
    const mult = sortOrder === 'asc' ? 1 : -1;

    const getSortValue = (component, key) => {
      switch (key) {
        case 'code':
          return (component.code || '').toLowerCase();
        case 'partNumber':
          return (component.partNumber || '').toLowerCase();
        case 'serialNumber':
          return (component.serialNumber || '').toLowerCase();
        case 'description':
          return (component.description || '').toLowerCase();
        case 'assembly':
          return (component.assemblyId?.typeName || '').toLowerCase();
        case 'position':
          return (component.position || '').toLowerCase();
        case 'status':
          return (component.status || '').toLowerCase();
        case 'active':
          return component.isActive !== false ? 'active' : 'inactive';
        case 'remaining':
          return getRemainingDisplay(component).toLowerCase();
        case 'remainingDays':
        case 'remainingHours':
        case 'remainingCycles':
        case 'remainingLandings': {
          const id = String(component._id || component.code || '');
          const rbu = remainingByUnitById.get(id) || {};
          const unitKey =
            key === 'remainingDays'
              ? 'days'
              : key === 'remainingHours'
                ? 'hours'
                : key === 'remainingCycles'
                  ? 'cycles'
                  : 'landings';
          const v = rbu[unitKey];
          return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
        }
        default:
          return '';
      }
    };

    list.sort((a, b) => {
      const va = getSortValue(a, sortBy);
      const vb = getSortValue(b, sortBy);
      const aNull = va == null || va === '';
      const bNull = vb == null || vb === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return mult * (va - vb);
      }
      return mult * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });

    return list;
  }, [filteredComponents, sortBy, sortOrder, remainingByUnitById]);

  const paginatedComponents = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedComponents.slice(start, start + rowsPerPage);
  }, [sortedComponents, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, components.length]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
        <Typography variant="h6">
          Components for {selectedAircraft.registration}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBackfillInventory && (
            <Button variant="outlined" startIcon={<Sync />} onClick={onBackfillInventory}>
              Backfill Inventory
            </Button>
          )}
          {canEdit && (
            <Button variant="contained" startIcon={<Add />} onClick={onAddComponent}>
              Add Component
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search components..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 220, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Assembly</InputLabel>
          <Select
            value={assemblyFilter}
            label="Assembly"
            onChange={handleAssemblyFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            {assemblyOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Active</InputLabel>
          <Select
            value={activeFilter}
            label="Active"
            onChange={handleActiveFilterChange}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Time Since Type</InputLabel>
          <Select
            value={timeSinceTypeFilter}
            label="Time Since Type"
            onChange={handleTimeSinceTypeFilterChange}
          >
            <MenuItem value="ALL">All Types</MenuItem>
            {timeSinceTypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Time Since Label</InputLabel>
          <Select
            value={timeSinceLabelFilter}
            label="Time Since Label"
            onChange={handleTimeSinceLabelFilterChange}
          >
            <MenuItem value="ALL">All Labels</MenuItem>
            {timeSinceLabelOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sortDirection={sortBy === 'code' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'code'}
                  direction={sortBy === 'code' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('code')}
                >
                  Component ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'partNumber' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'partNumber'}
                  direction={sortBy === 'partNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('partNumber')}
                >
                  Part Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'serialNumber' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'serialNumber'}
                  direction={sortBy === 'serialNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('serialNumber')}
                >
                  Serial no
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'description' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'description'}
                  direction={sortBy === 'description' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'assembly' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'assembly'}
                  direction={sortBy === 'assembly' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('assembly')}
                >
                  Assembly
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'position' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'position'}
                  direction={sortBy === 'position' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('position')}
                >
                  Position
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'active' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'active'}
                  direction={sortBy === 'active' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('active')}
                >
                  Active/Archived
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'remaining' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'remaining'}
                  direction={sortBy === 'remaining' ? sortOrder : 'asc'}
                  onClick={() => handleSortRequest('remaining')}
                >
                  Remaining
                </TableSortLabel>
              </TableCell>
              {COMPONENT_UNIT_COLUMN_DEFS.map((col) => (
                <TableCell key={col.id} sortDirection={sortBy === col.id ? sortOrder : false}>
                  <Tooltip title={`Soonest remaining in ${col.label.toLowerCase()} across this component's Time Since Records (live when available). Click to sort.`}>
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell sx={{ minWidth: 180 }}>
                <Tooltip title="For Years or Calendar Days TSRs: start (or as-on) through projected calendar due (As On + remaining). One line per record.">
                  <span>Due (calendar)</span>
                </Tooltip>
              </TableCell>
              <TableCell>Due At Parent Assembly</TableCell>
              <TableCell>Due At Component Value</TableCell>
              <TableCell>Due At Airframe Value</TableCell>
              <TableCell>Remaining until Due (Live)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredComponents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center">No components found</TableCell>
              </TableRow>
            ) : (
              paginatedComponents.map((component) => (
                <>
                  <TableRow
                    key={component._id}
                    hover
                    onClick={onRowClick ? () => onRowClick(component) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': onRowClick ? { backgroundColor: 'action.hover' } : undefined,
                      opacity: component.isActive === false ? 0.7 : 1
                    }}
                  >
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleExpand(component._id);
                        }}
                        aria-label={expandedComponentId === component._id ? 'Collapse' : 'Expand'}
                      >
                        <ExpandMore
                          sx={{
                            transform: expandedComponentId === component._id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {component.code}
                    </TableCell>
                  <TableCell>{component.partNumber}</TableCell>
                  <TableCell>{component.serialNumber || '—'}</TableCell>
                  <TableCell>{component.description}</TableCell>
                  <TableCell>{component.assemblyId?.typeName || '-'}</TableCell>
                  <TableCell>{component.position}</TableCell>
                  <TableCell>
                    <Chip
                      label={component.status}
                      color={getStatusColor(component.status)}
                      size="small"
                    />
                  </TableCell>
                    <TableCell>
                      <Tooltip title={component.isActive !== false ? 'This component is currently Active.' : 'This component is currently Inactive (removed from aircraft).'}>
                        <span>
                          <Chip
                            label={component.isActive !== false ? 'Active' : 'Inactive'}
                            color={component.isActive !== false ? 'success' : 'default'}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 260, whiteSpace: 'pre-line' }}>
                      <Tooltip title="Summary of remaining values for this component across all Time Since Records (one record per line: name: value unit).">
                        <span>{getRemainingDisplay(component)}</span>
                      </Tooltip>
                    </TableCell>
                    {COMPONENT_UNIT_COLUMN_DEFS.map((col) => {
                      const id = String(component._id || component.code || '');
                      const rbu = remainingByUnitById.get(id) || {};
                      const unitValue = rbu[col.unitKey];
                      const label = formatRemainingUnitCell(col.unitKey, unitValue);
                      return (
                        <TableCell key={col.id} sx={{ whiteSpace: 'nowrap' }}>
                          {label != null ? (
                            <Chip
                              label={label}
                              size="small"
                              color={getRemainingUnitChipColor(col.unitKey, unitValue)}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 280 }}>
                      <Tooltip title="Calendar due ranges for Years / Calendar Days TSRs. Red if due in ≤7 days or overdue; green otherwise. NIL when none apply.">
                        <span>{renderCalendarDueDisplay(component)}</span>
                      </Tooltip>
                    </TableCell>
                    {(() => {
                      const summaries = getEarliestDueSummaries(
                        component,
                        assemblies,
                        currentUsageTotals,
                        effectiveTlbUsageMapping
                      );
                      const hasNegativeRemaining =
                        summaries.airframeRemainingDue !== 'N/A' &&
                        summaries.airframeRemainingDue.split('\n').some((line) => /:\s*-/.test(line));
                      return (
                        <>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
                            <Tooltip
                              title={
                                summaries.assemblyDue !== 'N/A'
                                  ? 'Stored snapshot view: Due At Parent Assembly for this component. Each line shows one Time Since Record: label: value unit, using Parent Assembly Value at Installation + Component Remaining.'
                                  : 'Stored snapshot view: Due At Parent Assembly. N/A when no Time Since Record has both a Parent Assembly Value at Installation and Remaining.'
                              }
                            >
                              <span>{summaries.assemblyDue}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
                            <Tooltip
                              title={
                                summaries.componentDue !== 'N/A'
                                  ? 'Stored snapshot view: Due At Component Value. Each line shows one Time Since Record: label: value unit, based on the component\'s own limit or elapsed + remaining.'
                                  : 'Stored snapshot view: Due At Component Value. N/A when no Time Since Record has enough data (no limit and no elapsed/remaining).'
                              }
                            >
                              <span>{summaries.componentDue}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
                            <Tooltip
                              title={
                                summaries.airframeDue !== 'N/A'
                                  ? 'Live view: Due At Airframe Value for this component. Each line shows one Time Since Record: label: value unit, computed as airframeAtInstallation + (componentLimit - componentElapsedAtInstallation) using approved TLB totals (mapped per Client Settings) and TSR limit.value.'
                                  : 'Live view: Due At Airframe Value. N/A when live/current aircraft usage totals are missing, the mapped TLB field has no value, or the TSR row is missing airframeValueAtInstallation/limit/component elapsed-at-installation inputs.'
                              }
                            >
                              <span>{summaries.airframeDue}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-line',
                              color: hasNegativeRemaining ? 'error.main' : undefined,
                              fontWeight: hasNegativeRemaining ? 700 : undefined
                            }}
                          >
                            <Tooltip
                              title={
                                summaries.airframeRemainingDue !== 'N/A'
                                  ? 'Live view: Remaining until Due (Live) for this component. Each line shows one Time Since Record: label: value unit, computed as (Due_airframe_total - currentAirframe_live). Negative values indicate overdue.'
                                  : 'Live view: Remaining until Due (Live). N/A when required live/current usage keys or TSR limit.value are missing (even if remainingTime exists).'
                              }
                            >
                              <span>{summaries.airframeRemainingDue}</span>
                            </Tooltip>
                          </TableCell>
                        </>
                      );
                    })()}
                    <TableCell>
                    {canEdit && (
                      <>
                        <IconButton 
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onEditComponent(component); }}
                          title="Edit Component"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onOpenDocumentDialog('COMPONENT', component); }}
                          title="Manage Documents"
                        >
                          <Description />
                        </IconButton>
                        {onDeleteComponent && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); onDeleteComponent(component); }}
                            title={component.isActive !== false ? "Archive Component" : "Permanently Delete Component"}
                          >
                            <Delete />
                          </IconButton>
                        )}
                        {onAddToInventory && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); onAddToInventory(component); }}
                            title="Add to Aircraft Inventory"
                          >
                            <AddShoppingCart />
                          </IconButton>
                        )}
                      </>
                    )}
                    </TableCell>
                  </TableRow>
                  {expandedComponentId === component._id && (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} sx={{ bgcolor: 'action.hover', p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Time Since Record Comparisons
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Label</TableCell>
                              <TableCell>Unit</TableCell>
                              <TableCell>Assembly Value at Install</TableCell>
                              <TableCell>Due At Assembly Value</TableCell>
                              <TableCell>Component Value at Install</TableCell>
                              <TableCell>Component Value at Due</TableCell>
                              <TableCell>Airframe Value at Install</TableCell>
                              <TableCell>Due At Airframe Value</TableCell>
                              <TableCell>Remaining until Due (Live)</TableCell>
                              <TableCell>Due (calendar)</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(component.timeSinceRecords || []).map((record, idx) => {
                              const componentComparison = computeComponentSelfComparison(record);
                              const unit =
                                record.elapsedTime?.unit ||
                                record.limit?.unit ||
                                record.remainingTime?.unit ||
                                record.installedValue?.unit ||
                                '';
                              const dueComponentUnit = record.limit?.unit || unit;

                              const parentSnapshotStr = record.parentAssemblyValueAtInstallation?.value;
                              const airframeSnapshotStr = record.airframeValueAtInstallation?.value;
                              const remainingStr = record.remainingTime?.value;
                              const limitRaw = record?.limit?.value;
                              const currentValues = currentUsageTotals?.values || null;
                              const { raw: liveRaw, key: liveKey, reason: liveMapReason } = getLiveRawForTsr(
                                currentValues,
                                effectiveTlbUsageMapping,
                                liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
                              );

                              const assemblyInstallVal =
                                parentSnapshotStr != null && String(parentSnapshotStr).trim() !== ''
                                  ? parentSnapshotStr
                                  : 'N/A';
                              const airframeInstallVal =
                                airframeSnapshotStr != null && String(airframeSnapshotStr).trim() !== ''
                                  ? airframeSnapshotStr
                                  : 'N/A';

                              let assemblyDueVal = 'N/A';
                              let airframeDueVal = 'N/A';
                              let airframeRemainingVal = 'N/A';
                              let airframeRemainingNumeric = null;

                              if (
                                parentSnapshotStr != null &&
                                String(parentSnapshotStr).trim() !== '' &&
                                remainingStr != null &&
                                String(remainingStr).trim() !== '' &&
                                unit
                              ) {
                                try {
                                  const installedNumeric = parseValueByUnit(parentSnapshotStr, unit);
                                  const remainingNumeric = parseValueByUnit(remainingStr, unit);
                                  const dueNumeric = installedNumeric + remainingNumeric;
                                  assemblyDueVal = formatValueByUnit(dueNumeric, unit);
                                } catch (e) {
                                  assemblyDueVal = 'N/A';
                                }
                              }

                              const limitPresent = limitRaw != null && String(limitRaw).trim() !== '';
                              const airframeAtInstallPresent = airframeSnapshotStr != null && String(airframeSnapshotStr).trim() !== '';
                              const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;
                              const componentElapsedAtInstallPresent =
                                componentElapsedAtInstallRaw != null && String(componentElapsedAtInstallRaw).trim() !== '';

                              if (liveRaw != null && limitPresent && airframeAtInstallPresent && componentElapsedAtInstallPresent) {
                                try {
                                  const airframeLiveNumeric = parseValueByUnit(liveRaw, unit);
                                  const airframeAtInstallNumeric = parseValueByUnit(airframeSnapshotStr, unit);
                                  const componentElapsedAtInstallNumeric = parseValueByUnit(componentElapsedAtInstallRaw, unit);
                                  const limitNumeric = parseValueByUnit(limitRaw, unit);

                                  const dueNumeric = airframeAtInstallNumeric + (limitNumeric - componentElapsedAtInstallNumeric);
                                  const remainingNumeric = dueNumeric - airframeLiveNumeric;

                                  airframeDueVal = formatValueByUnit(dueNumeric, unit);
                                  airframeRemainingVal = formatSignedValueByUnit(remainingNumeric, unit);
                                  airframeRemainingNumeric = remainingNumeric;
                                } catch (e) {
                                  airframeDueVal = 'N/A';
                                  airframeRemainingVal = 'N/A';
                                  airframeRemainingNumeric = null;
                                }
                              }

                              const componentInstallVal = componentComparison.installedAtComponent != null && componentComparison.installedAtComponent !== ''
                                ? componentComparison.installedAtComponent
                                : 'N/A';
                              const componentDueVal = componentComparison.dueAtComponent != null && componentComparison.dueAtComponent !== ''
                                ? componentComparison.dueAtComponent
                                : 'N/A';
                              
                              const tooltipAssemblyInstall = assemblyInstallVal !== 'N/A'
                                ? `Stored snapshot: Parent Assembly's Value at Installation for this component (Installed At Assembly Value). Value: ${assemblyInstallVal} ${unit}.`
                                : 'Stored snapshot: Parent Assembly\'s Value at Installation for this component (Installed At Assembly Value). N/A when no snapshot value was stored.';
                              const tooltipAssemblyDue = assemblyDueVal !== 'N/A'
                                ? `Stored snapshot view: Due At Assembly Value for this component. Formula (from snapshot): Parent Assembly Value at Installation + Component Remaining. Value: ${assemblyDueVal} ${unit}.`
                                : 'Stored snapshot view: Due At Assembly Value for this component. Formula (from snapshot): Parent Assembly Value at Installation + Component Remaining. N/A when snapshot or remaining data is missing.';
                              const tooltipComponentInstall = componentInstallVal !== 'N/A'
                                ? `This component's elapsed value at installation (from this TSR). Value: ${componentInstallVal} ${unit}.`
                                : `This component's elapsed value at installation (from this TSR). N/A when no elapsed value stored for this record.`;
                              const tooltipComponentDue = componentDueVal !== 'N/A'
                                ? `This component's value at due. Formula: Limit value if set; otherwise elapsed + remaining. Value: ${componentDueVal} ${dueComponentUnit}.`
                                : `This component's value at due. Formula: Limit value if set; otherwise elapsed + remaining. N/A when no limit and no elapsed/remaining.`;
                              const tooltipAirframeInstall = airframeInstallVal !== 'N/A'
                                ? `Stored snapshot: Airframe Value at Installation for this component. Value: ${airframeInstallVal} ${unit}.`
                                : 'Stored snapshot: Airframe Value at Installation for this component. N/A when no snapshot value was stored.';
                              const tooltipAirframeDue = (() => {
                                if (!currentUsageTotals?.values || typeof currentUsageTotals.values !== 'object') {
                                  return 'N/A: Live/current aircraft usage totals are not available.';
                                }
                                if (liveRaw == null) {
                                  return liveMapReason
                                    ? `N/A: ${liveMapReason}`
                                    : `N/A: No live value for TLB field '${liveKey || '?'}' in currentUsageTotals.values.`;
                                }
                                if (!limitPresent) {
                                  return 'N/A: Due At Airframe Value is computed from TSR limit.value (component life limit/frequency). This TSR row has no limit.value; remainingTime does not drive the live due calculation.';
                                }

                                if (airframeSnapshotStr == null || String(airframeSnapshotStr).trim() === '' || !componentElapsedAtInstallPresent) {
                                  return 'N/A: Live Due/Remaining requires airframeValueAtInstallation and this component elapsed at installation (elapsedTime.value).';
                                }

                                return airframeDueVal !== 'N/A'
                                  ? `Live view: Due At Airframe Value for this component. Formula: Due_airframe = Airframe_at_install + (Component_limit - Component_elapsed_at_install). Value: ${airframeDueVal} ${unit}.`
                                  : 'N/A: Unable to compute due from live + installation snapshots.';
                              })();

                              const calendarDueLine = formatTsrCalendarDueRangeDisplay(record);

                              const tooltipAirframeRemaining = (() => {
                                if (!currentUsageTotals?.values || typeof currentUsageTotals.values !== 'object') {
                                  return 'N/A: Live/current aircraft usage totals are not available.';
                                }
                                if (liveRaw == null) {
                                  return liveMapReason
                                    ? `N/A: ${liveMapReason}`
                                    : `N/A: No live value for TLB field '${liveKey || '?'}' in currentUsageTotals.values.`;
                                }
                                const limitPresent = limitRaw != null && String(limitRaw).trim() !== '';
                                if (!limitPresent) {
                                  return 'N/A: Remaining until Due is computed from TSR limit.value (component life limit/frequency). This TSR row has no limit.value; remainingTime does not drive the live remaining calculation.';
                                }
                                const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;
                                const componentElapsedAtInstallPresent =
                                  componentElapsedAtInstallRaw != null && String(componentElapsedAtInstallRaw).trim() !== '';

                                if (airframeSnapshotStr == null || String(airframeSnapshotStr).trim() === '' || !componentElapsedAtInstallPresent) {
                                  return 'N/A: Live Due/Remaining requires airframeValueAtInstallation and this component elapsed at installation (elapsedTime.value).';
                                }

                                return airframeRemainingVal !== 'N/A'
                                  ? `Live view: Remaining until Due (Live). Formula: Remaining = Due_airframe_total - currentAirframe_live. Value: ${airframeRemainingVal} ${unit}.`
                                  : 'N/A: Unable to compute remaining from live + installation snapshots.';
                              })();

                              return (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Tooltip title="Time Since Type of this record (e.g. Hours, Cycles).">
                                      <span>{record.type}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Time Since Label of this record.">
                                      <span>{record.label}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Unit for this TSR (Hours, Years, Calendar Days, Cycles, Landings, Hobbs Meter, Hobbs Meter AC).">
                                      <span>{unit}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipAssemblyInstall}>
                                      <span>{assemblyInstallVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipAssemblyDue}>
                                      <span>{assemblyDueVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipComponentInstall}>
                                      <span>{componentInstallVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipComponentDue}>
                                      <span>{componentDueVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipAirframeInstall}>
                                      <span>{airframeInstallVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={tooltipAirframeDue}>
                                      <span>{airframeDueVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: airframeRemainingNumeric != null && airframeRemainingNumeric < 0 ? 'error.main' : undefined,
                                      fontWeight: airframeRemainingNumeric != null && airframeRemainingNumeric < 0 ? 700 : undefined
                                    }}
                                  >
                                    <Tooltip title={tooltipAirframeRemaining}>
                                      <span>{airframeRemainingVal}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
                                    <Tooltip title="Years / Calendar Days: start → projected due from As On + remaining. Red if due in ≤7 days or overdue; green otherwise.">
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: calendarDueLine ? getCalendarDueRangeMuiColor(record) : 'text.secondary'
                                        }}
                                      >
                                        {calendarDueLine || '—'}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {/* Notes column intentionally left blank for snapshot view */}
                                    <span />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
        {sortedComponents.length > 0 && (
          <TablePagination
            component="div"
            count={sortedComponents.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />
        )}
      </TableContainer>
    </>
  );
};

export default ComponentTable;

